<template>
  <div class="page-view">
    <div class="header-row">
      <div>
        <h2>个人中心</h2>
        <p>查看并维护当前登录账号的姓名、手机号和登录密码。</p>
      </div>
      <el-button type="primary" plain @click="loadProfile">刷新</el-button>
    </div>

    <div class="profile-grid">
      <div class="brand-card panel-card">
        <div class="section-row">
          <h3>个人信息</h3>
          <el-button size="mini" type="primary" plain @click="openProfileDialog">编辑信息</el-button>
        </div>
        <div class="info-grid">
          <div><span>账号</span><strong>{{ profile.username || "-" }}</strong></div>
          <div><span>姓名</span><strong>{{ profile.name || "-" }}</strong></div>
          <div><span>手机号</span><strong>{{ profile.phone || "-" }}</strong></div>
          <div><span>角色</span><strong>{{ roleText }}</strong></div>
          <div><span>区域</span><strong>{{ profile.region || "-" }}</strong></div>
          <div><span>抢单权限</span><strong>{{ grabModeText }}</strong></div>
        </div>
      </div>

      <div class="brand-card panel-card">
        <div class="section-row">
          <h3>安全设置</h3>
          <el-button size="mini" type="warning" plain @click="openPasswordDialog">修改密码</el-button>
        </div>
        <div class="security-box">
          <p>登录密码：已隐藏</p>
          <p>密码修改后，下次登录请使用新密码。</p>
          <el-button size="mini" plain @click="logout">退出登录</el-button>
        </div>
      </div>
    </div>

    <el-dialog title="编辑个人信息" :visible.sync="showProfileDialog" width="520px">
      <el-form ref="profileFormRef" :model="profileForm" :rules="profileRules" label-width="90px">
        <el-form-item label="姓名" prop="name">
          <el-input v-model="profileForm.name" maxlength="30" />
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="profileForm.phone" maxlength="11" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showProfileDialog = false">取消</el-button>
        <el-button type="primary" :loading="savingProfile" @click="submitProfile">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="修改密码" :visible.sync="showPasswordDialog" width="520px">
      <el-form ref="passwordFormRef" :model="passwordForm" :rules="passwordRules" label-width="100px">
        <el-form-item label="原密码" prop="old_password">
          <el-input v-model="passwordForm.old_password" show-password />
        </el-form-item>
        <el-form-item label="新密码" prop="new_password">
          <el-input v-model="passwordForm.new_password" show-password />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirm_password">
          <el-input v-model="passwordForm.confirm_password" show-password />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showPasswordDialog = false">取消</el-button>
        <el-button type="primary" :loading="savingPassword" @click="submitPassword">确认修改</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { changeMyPassword, fetchMyProfile, updateMyProfile } from "../api/orders";
import { getStoredUser, roleLabel } from "../utils/auth";

const emptyProfileForm = () => ({
  name: "",
  phone: "",
});

const emptyPasswordForm = () => ({
  old_password: "",
  new_password: "",
  confirm_password: "",
});

export default {
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (!value) {
        callback(new Error("请再次输入新密码"));
        return;
      }
      if (value !== this.passwordForm.new_password) {
        callback(new Error("两次输入的密码不一致"));
        return;
      }
      callback();
    };

    return {
      profile: getStoredUser(),
      showProfileDialog: false,
      showPasswordDialog: false,
      savingProfile: false,
      savingPassword: false,
      profileForm: emptyProfileForm(),
      passwordForm: emptyPasswordForm(),
      profileRules: {
        name: [{ required: true, message: "请输入姓名", trigger: "blur" }],
        phone: [
          { required: true, message: "请输入手机号", trigger: "blur" },
          { pattern: /^1\d{10}$/, message: "请输入11位手机号", trigger: "blur" },
        ],
      },
      passwordRules: {
        old_password: [{ required: true, message: "请输入原密码", trigger: "blur" }],
        new_password: [
          { required: true, message: "请输入新密码", trigger: "blur" },
          { min: 6, message: "新密码至少6位", trigger: "blur" },
        ],
        confirm_password: [
          { required: true, message: "请再次输入新密码", trigger: "blur" },
          { validator: validateConfirmPassword, trigger: "blur" },
        ],
      },
    };
  },
  computed: {
    roleText() {
      return roleLabel(this.profile.role);
    },
    grabModeText() {
      if (this.profile.grab_mode === "grab_only") return "只抢单";
      if (this.profile.grab_mode === "assign_and_grab") return "分单+抢单";
      return "只分单";
    },
  },
  mounted() {
    this.loadProfile();
  },
  methods: {
    async loadProfile() {
      const { data } = await fetchMyProfile();
      this.profile = data;
      localStorage.setItem("ps_user", JSON.stringify(data));
    },
    openProfileDialog() {
      this.profileForm = {
        name: this.profile.name || "",
        phone: this.profile.phone || "",
      };
      this.showProfileDialog = true;
      this.$nextTick(() => this.$refs.profileFormRef?.clearValidate());
    },
    openPasswordDialog() {
      this.passwordForm = emptyPasswordForm();
      this.showPasswordDialog = true;
      this.$nextTick(() => this.$refs.passwordFormRef?.clearValidate());
    },
    submitProfile() {
      this.$refs.profileFormRef.validate(async (valid) => {
        if (!valid) return;
        this.savingProfile = true;
        try {
          const { data } = await updateMyProfile(this.profileForm);
          this.profile = data;
          localStorage.setItem("ps_user", JSON.stringify(data));
          this.$message.success("个人信息已更新");
          this.showProfileDialog = false;
        } catch (error) {
          this.$message.error(error.response?.data?.detail || "保存失败");
        } finally {
          this.savingProfile = false;
        }
      });
    },
    submitPassword() {
      this.$refs.passwordFormRef.validate(async (valid) => {
        if (!valid) return;
        this.savingPassword = true;
        try {
          await changeMyPassword({
            old_password: this.passwordForm.old_password,
            new_password: this.passwordForm.new_password,
          });
          this.$message.success("密码修改成功");
          this.showPasswordDialog = false;
          this.passwordForm = emptyPasswordForm();
        } catch (error) {
          this.$message.error(error.response?.data?.detail || "修改失败");
        } finally {
          this.savingPassword = false;
        }
      });
    },
    logout() {
      localStorage.removeItem("ps_token");
      localStorage.removeItem("ps_user");
      this.$router.replace("/login");
    },
  },
};
</script>

<style scoped>
.header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; }
.header-row h2 { margin: 0 0 8px; }
.header-row p { margin: 0; color: #60718f; }
.profile-grid { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 18px; }
.panel-card { padding: 20px; }
.section-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; }
.section-row h3 { margin: 0; }
.info-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.info-grid span { display: block; margin-bottom: 6px; color: #60718f; font-size: 13px; }
.info-grid strong { font-size: 15px; color: #1d2b53; }
.security-box {
  padding: 18px;
  border-radius: 16px;
  background: #f6f9ff;
  color: #385074;
}
.security-box p {
  margin: 0 0 12px;
}
.security-box p:last-of-type {
  margin-bottom: 18px;
}
</style>
